// Librairies
import { Component, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, debounceTime, defer, map, Observable, of, shareReplay, Subject, switchMap } from 'rxjs';

// Interfaces
export interface MemberOption {
  id: string;
  label: string;
  sublabel?: string;
}

const normalize = (text: string): string => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// The options whose label or sublabel holds the query (case and accents ignored), the excluded ones left out before the limit.
export function filterOptions(options: MemberOption[], q: string, limit: number = 20, excludeIds: string[] = []): MemberOption[] {
  const query = normalize(q.trim());
  const excluded = new Set(excludeIds);
  return options.filter(option => !excluded.has(option.id) && normalize(`${option.label} ${option.sublabel ?? ''}`).includes(query)).slice(0, limit);
}

export type MemberSearch = (q: string, excludeIds: string[]) => Observable<MemberOption[]>;

// A search over a list loaded once, at the first search, then filtered here (loaded again after a failure).
export function localSearch(load: () => Observable<MemberOption[]>, limit: number = 20): MemberSearch {
  const options$ = defer(load).pipe(shareReplay(1));
  return (q, excludeIds) => options$.pipe(map(options => filterOptions(options, q, limit, excludeIds)));
}

// Search field picking one option at a time (the search function may query the server or filter locally).
@Component({
  selector: 'app-member-search',
  standalone: true,
  imports: [
    FormsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    TranslateModule
  ],
  templateUrl: './member-search.component.html',
  styleUrl: './member-search.component.scss'
})

export class MemberSearchComponent {
  // Signals
  readonly placeholder = input.required<string>();
  readonly search = input.required<MemberSearch>();
  readonly excludeIds = input<string[]>([]);
  readonly picked = output<MemberOption>();
  readonly options = signal<MemberOption[]>([]);

  // Variables
  readonly search$: Subject<string> = new Subject<string>();
  text: string = '';

  constructor() {
    this.search$.pipe(debounceTime(250), switchMap(q => (typeof q === 'string' ? this.search()(q.trim(), this.excludeIds()) : of([])).pipe(catchError(() => of([])))), takeUntilDestroyed(inject(DestroyRef))).subscribe(options => this.options.set(options));
  }

  pick(event: MatAutocompleteSelectedEvent): void {
    this.picked.emit(event.option.value);
    this.text = '';
    this.options.set([]);
  }
}
